import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Eye, EyeOff, Loader2, AlertCircle, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { authAPI, twoFaAPI, brandingAPI } from '../lib/api';

export default function LoginPage() {
  const { login, theme } = useApp();
  const navigate = useNavigate();
  const primary = theme?.primary || '#2563eb';

  // Imagem de fundo customizável (Configurações > Geral) — busca sem JWT,
  // então falha silenciosamente e mantém o fundo default se indisponível.
  const [loginBg, setLoginBg] = useState('');
  useEffect(() => {
    let cancelled = false;
    brandingAPI.getPublic().then(({ data }) => {
      if (!cancelled && data?.login_background_url) setLoginBg(data.login_background_url);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Estado do desafio de 2FA — presente somente quando o backend responde
  // { two_fa_required: true, pending_token }
  const [twoFa, setTwoFa] = useState(null); // { pending_token }
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaError, setTwoFaError] = useState('');
  const twoFaInputRef = useRef(null);

  useEffect(() => {
    if (twoFa && twoFaInputRef.current) {
      setTimeout(() => twoFaInputRef.current?.focus(), 30);
    }
  }, [twoFa]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Preencha todos os campos.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.login({ email: form.email, password: form.password });
      const data = res.data || {};
      // Desafio de 2FA: backend retorna pending_token em vez de token
      if (data.two_fa_required && data.pending_token) {
        setTwoFa({ pending_token: data.pending_token });
        setTwoFaCode('');
        setTwoFaError('');
        return;
      }
      if (data.token) {
        login(data.user, data.token);
        navigate('/app');
        return;
      }
      setError('Resposta inesperada do servidor.');
    } catch (err) {
      const status = err.response?.status;
      const code = err.code; // 'ECONNABORTED' | 'ERR_NETWORK' | ...
      const backendMsg = err.response?.data?.error || err.response?.data?.message;
      if (status === 401 || status === 403) {
        setError('Credenciais inválidas.');
      } else if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
        setError('Servidor lento demais para responder — tente novamente em alguns segundos.');
      } else if (code === 'ERR_NETWORK' || !err.response) {
        setError('Servidor indisponível no momento. Tente novamente em instantes.');
      } else if (backendMsg) {
        setError(backendMsg);
      } else {
        setError('Falha ao fazer login. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTwoFa = async (e) => {
    e?.preventDefault?.();
    const code = twoFaCode.trim();
    if (code.length < 6) {
      setTwoFaError('Informe o código de 6 dígitos.');
      return;
    }
    setLoading(true);
    setTwoFaError('');
    try {
      const res = await twoFaAPI.loginVerify(twoFa.pending_token, code);
      const data = res.data || {};
      if (data.token) {
        login(data.user, data.token);
        navigate('/app');
        return;
      }
      setTwoFaError('Resposta inesperada do servidor.');
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.response?.data?.message
        || (status === 401 ? 'Código incorreto ou expirado.' : 'Falha ao validar o código 2FA.');
      setTwoFaError(msg);
    } finally {
      setLoading(false);
    }
  };

  const cancelTwoFa = () => {
    setTwoFa(null);
    setTwoFaCode('');
    setTwoFaError('');
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'var(--bg)' }}
    >
      {loginBg && (
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          data-testid="login-background-image"
        />
      )}
      <div
        className="absolute inset-0"
        style={{
          background: loginBg ? 'rgba(0,0,0,0.55)' : 'transparent',
          backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -20%, ${primary}22 0%, transparent 70%)`,
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}bb 100%)` }}
            >
              <Radio size={26} className="text-white" />
            </div>
            <span className="font-display font-black text-xl tracking-tight text-[var(--text-primary)]">
              OLT MANAGER
            </span>
          </Link>
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            {twoFa ? 'Verificação em dois fatores' : 'Faça login para acessar o painel'}
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-[var(--border-mid)] bg-[var(--surface)] shadow-2xl p-8">
          <AnimatePresence mode="wait" initial={false}>
            {!twoFa ? (
              <motion.div
                key="credentials-step"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.18 }}
              >
                <h1 className="text-xl font-bold text-[var(--text-primary)] mb-6">Entrar na conta</h1>

                {error && (
                  <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400" data-testid="login-error">
                    <AlertCircle size={15} className="shrink-0" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">E-mail</label>
                    <input
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="admin@exemplo.com"
                      data-testid="login-email"
                      className="w-full h-11 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': primary }}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Senha</label>
                      <Link to="/forgot-password" className="text-xs font-medium hover:underline" style={{ color: primary }}>
                        Esqueci minha senha
                      </Link>
                    </div>
                    <div className="relative">
                      <input
                        type={showPass ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        placeholder="••••••••"
                        data-testid="login-password"
                        className="w-full h-11 px-4 pr-11 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPass((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                        tabIndex={-1}
                      >
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit"
                    className="w-full h-11 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {loading ? 'Entrando...' : 'Entrar'}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="twofa-step"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.18 }}
                data-testid="login-2fa-step"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-emerald-500/15 text-emerald-500 shrink-0">
                    <ShieldCheck size={22} />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-[var(--text-primary)] leading-tight">Código de verificação</h1>
                    <p className="text-xs text-[var(--text-muted)]">Digite o código de 6 dígitos do seu aplicativo autenticador.</p>
                  </div>
                </div>

                {twoFaError && (
                  <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400" data-testid="login-2fa-error">
                    <AlertCircle size={15} className="shrink-0" />
                    {twoFaError}
                  </div>
                )}

                <form onSubmit={handleVerifyTwoFa} className="space-y-4">
                  <input
                    ref={twoFaInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={8}
                    value={twoFaCode}
                    onChange={(e) => { setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 8)); setTwoFaError(''); }}
                    placeholder="000000"
                    data-testid="login-2fa-code"
                    className="w-full h-14 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-2xl font-mono tracking-[0.6em] text-center text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': primary }}
                  />
                  <div className="text-[11px] text-[var(--text-muted)] text-center">
                    Você também pode usar um dos seus <strong>códigos de backup</strong>.
                  </div>

                  <button
                    type="submit"
                    disabled={loading || twoFaCode.length < 6}
                    data-testid="login-2fa-submit"
                    className="w-full h-11 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                    style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {loading ? 'Verificando...' : 'Verificar e entrar'}
                  </button>

                  <button
                    type="button"
                    onClick={cancelTwoFa}
                    data-testid="login-2fa-cancel"
                    className="w-full h-10 rounded-xl text-xs font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)] inline-flex items-center justify-center gap-1.5"
                  >
                    <ArrowLeft size={12} /> Voltar ao login
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
